// src/components/KalendarObrady.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { getOrderId } from "../lib/orderId";
import ConflictModal from "./ConflictModal";
import { useFormData } from "../context/FormDataContext";
import { POSADKY } from "../data/posadky";
import { apiFetch as authApiFetch, logout as authLogout } from "../auth/authClient";


// -----------------------------
// 🔐 local token helper (pohrebka_token + fallbacky)
// -----------------------------
const __getAuthToken = () => {
  try {
    const v =
      (localStorage.getItem("pohrebka_token") ||
       localStorage.getItem("token") ||
       localStorage.getItem("authToken") ||
       localStorage.getItem("accessToken") ||
       localStorage.getItem("jwt") ||
       "");
    return String(v || "").replace(/^Bearer\s+/i, "").trim();
  } catch (e) {
    return "";
  }
};

import { parsePosadka, overlap, intersect } from "../utils/posadka";

function validatePosadkaCollision({ edited, allEvents }) {
  const editedStart = new Date(edited.start);
  const editedEnd = new Date(edited.end);

  const editedLabel =
    (edited.posadkaLabel || "").trim() ||
    (edited.posadkaRucne || "").trim() ||
    (edited.posadkaNazev || "").trim() ||
    (edited.posadka || "").trim();

  const editedCrew =
    (edited.posadkaClenove && edited.posadkaClenove.length)
      ? edited.posadkaClenove
      : parsePosadka(editedLabel);

  if (!editedCrew.length) return null;

  for (const ev of allEvents || []) {
    if (!ev) continue;
    if (ev.id && edited.id && ev.id === edited.id) continue;

    const evStart = new Date(ev.start);
    const evEnd = new Date(ev.end);
    if (!overlap(editedStart, editedEnd, evStart, evEnd)) continue;

    const evLabel =
      (ev.posadkaLabel || "").trim() ||
      (ev.posadkaRucne || "").trim() ||
      (ev.posadkaNazev || "").trim() ||
      (ev.posadka || "").trim();

    const evCrew =
      (ev.posadkaClenove && ev.posadkaClenove.length)
        ? ev.posadkaClenove
        : parsePosadka(evLabel);

    const clash = intersect(editedCrew, evCrew);
    if (clash.length) {
      const who = clash.join(", ");
      const other = ev.zakazkaCislo || ev.cisloZakazky || ev.title || ev.id || "jiná zakázka";
      const t1 = evStart.toLocaleTimeString("cs-CZ", { hour: "2-digit", minute: "2-digit" });
      const t2 = evEnd.toLocaleTimeString("cs-CZ", { hour: "2-digit", minute: "2-digit" });
      return `Kolize posádky: ${who} už je přiřazen u ${other} (${t1}–${t2}).`;
    }
  }

  return null;
}

// ✅ původní vzhled (čistý, přehledný)
const card = { border: "1px solid #ddd", borderRadius: 12, padding: 12, background: "#fff" };
const input = { width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #ccc", outline: "none" };
const label = { fontSize: 13, fontWeight: 900, marginBottom: 6, display: "block" };
const btn = { padding: "10px 14px", borderRadius: 10, border: "1px solid #333", cursor: "pointer", fontWeight: 900 };
const hint = { fontSize: 12, opacity: 0.75 };

function pad(n) {
  return String(n).padStart(2, "0");
}
function fmtDate(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function startOfWeek(date) {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7; // Po=0
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
}
function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

const safeStr = (v) => (v == null ? "" : String(v));
const pick = (...vals) => {
  for (const v of vals) {
    const s = safeStr(v).replace(/\s+/g, " ").trim();
    if (s) return s;
  }
  return "";
};

// ✅ jednotná JSON funkce přes authClient (token + base URL)
async function apiJson(path, opts = {}) {
  try {
    const headersIn = (opts && typeof opts === "object" ? opts.headers : null) || {};
    const headers = { "Content-Type": "application/json", ...headersIn };
      // ✅ FORCE AUTH (fallback): vždy přidej Bearer token, pokud existuje
      try {
        const t = __getAuthToken();
        if (t && !headers.Authorization) headers.Authorization = `Bearer ${t}`;
      } catch (e) {}


    const res = await authApiFetch(path, {
      ...opts,
      headers,
    });

    // authApiFetch může vracet buď JSON, nebo fetch Response
    if (res && typeof res.json === "function") {
      // Response -> udělej JSON + hlášky při chybě
      let data = null;
      try {
        data = await res.json();
      } catch (e) {
        // někdy může přijít prázdné tělo
        data = null;
      }

      if (res.status === 401) authLogout();

      if (!res.ok) {
        const msg = (data && (data.error || data.message)) || `HTTP ${res.status}`;
        const err = new Error(msg);
        err.status = res.status;
        err.data = data;
        throw err;
      }

      return data;
    }

    // už to je JSON (nebo objekt) -> vrať rovnou
    return resp;
  } catch (e) {
    if (e?.status === 401) authLogout();
    throw e;
  }
}

function buildZesnulyLabel(formData) {
  const z = formData?.zesnulaOsoba || {};
  const v = formData?.vstupniInformace || {};
  const j = pick(z?.jmeno, v?.jmenoZesnuleho, v?.jmeno);
  const p = pick(z?.prijmeni, v?.prijmeniZesnuleho, v?.prijmeni);
  return [p, j].filter(Boolean).join(" ").trim();
}

function buildTypLabel(formData) {
  const kp = formData?.kremacePohreb || {};
  const o = formData?.obrad || {};
  return pick(o?.typ, kp?.typObradu, kp?.obradTyp, kp?.typ, "");
}

// ✅ zajistí objednavkaId pro uložení obřadu (bez toho backend odmítne)
async function ensureObjednavkaId({ form, zakazkaId, ctxCurrentOrderId, formData, saveOrder }) {
  const pickId =
    (String(form?.objednavkaId || "").trim()) ||
    (String(zakazkaId || "").trim()) ||
    (String(ctxCurrentOrderId || "").trim()) ||
    (String(formData?.currentOrderId || "").trim()) ||
    (String(formData?.vstupniInformace?.zakazkaId || "").trim()) ||
    "";

  if (pickId) return pickId;

  // fallback: vytvoř zakázku v knize (vznikne ID)
  if (typeof saveOrder === "function") {
    const out = await saveOrder();
    // různé návraty – bereme cokoliv použitelného
    const newId =
      (typeof out === "string" ? out : "") ||
      String(out?.id || out?.orderId || out?.zakazkaId || out?.incomingId || "").trim();
    if (newId) return newId;
  }

  return "";
}


function normalizeCrew(s) {
  return safeStr(s).trim();
}

export default function KalendarObrady({zakazkaId = "", onOpenZakazka, mode = "full", onOpenObrad} = {}) {
  // === POSADKY/OBRADNICE RULES (Pavel) ===
  const FIX_POSADKY = ['Stanko + Novák', 'Děda + Burka'];
  const FIX_OBRADNICE = ['Mačková', 'Partiková', 'Ola'];
  const FIX_MUZI = ['Stanko', 'Novák', 'Děda', 'Burka'];
  const CK_MISTO = 'Obřadní síň Český Krumlov';

  const norm = (x) => String(x ?? "").trim();

  const splitCrew = (crewText) => {
    const t = norm(crewText);
    if (!t) return [];
    return t
      .split(/[+,/;]/g)
      .map((x) => x.trim())
      .filter(Boolean);
  };

  const crewOverlapPeople = (crewA, crewB) => {
    const A = new Set(splitCrew(crewA));
    const B = splitCrew(crewB);
    return B.filter((p) => A.has(p));
  };

  const peopleOfEvent = (e) => {
    const base = splitCrew(e?.posadka);
    const extra = norm(e?.predaniClovek);
    return extra ? [...base, extra] : base;
  };

  // vrací konkrétní jména, která se bijí mezi formulářem a eventem
  const overlapPeopleBetween = (formLike, ev) => {
    const extra = norm(formLike?.predaniClovek);
    const A = new Set([
      ...splitCrew(formLike?.posadka),
      ...(extra ? [extra] : []),
    ]);
    const B = peopleOfEvent(ev);
    return B.filter((p) => A.has(p));
  };

  // ✅ potřebujeme umět uložit do KNIHY -> tím vznikne ID zakázky
  const { formData, currentOrderId: ctxCurrentOrderId, saveOrder } = useFormData();
  const orderId = getOrderId({ formData, propsOrderId: (typeof props !== "undefined" ? props?.orderId : undefined) });

  // ✅ otevři zakázku z eventu v kalendáři (klik na řádek / tlačítko)
  const openZakazkaFromEvent = useCallback(async (ev) => {

    try {
      // 1) pokud už máme lokální přepárování obřadu -> použij ho
      try {
        const evId0 = String(ev?.id || "").trim();
        if (evId0) {
          const linked = localStorage.getItem(`obrad_link_${evId0}`);
          if (linked) {
            ev = { ...(ev || {}), objednavkaId: String(linked).trim() };
          }
        }
      } catch (_e) {}

      // 2) vyzobni ID zakázky z různých polí
      const id = String(
      ev?.objednavkaId ||
      ev?.zakazkaId ||
      ev?.cisloZakazky ||
      ev?.zakazkaCislo ||
      ev?.orderId ||
      ""
    ).trim();

    if (!id) {
      alert("U této události není dostupné ID zakázky (objednavkaId / zakazkaId).");
      return;
    }
      // 3) pokus o otevření (přes App.jsx callback)
      if (typeof onOpenZakazka === "function") {
        try {
          await onOpenZakazka(id);
    if (typeof onOpenObrad === "function") onOpenObrad();
        } catch (e) {
          // 404 = v Knize neexistuje -> nabídni auto-fix
          const msg = String(e?.message || "").toLowerCase();
          const status = e?.status || e?.data?.status || e?.data?.code || 0;
          const notFound = status === 404 || msg.includes("nenalez") || msg.includes("not found");

          if (notFound) {
            const ok = window.confirm(
              `Zakázka ${id} nebyla nalezena v Knize.\n\nChceš vytvořit novou zakázku z této události a napojit ji?`
            );
            if (!ok) throw e;

            // vytvoř zakázku v knize (vznikne nové ID)
            const out = await saveOrder?.();
            const newId =
              (typeof out === "string" ? out : "") ||
              String(out?.id || out?.orderId || out?.zakazkaId || "").trim();

            if (!newId) {
              alert("Nepodařilo se vytvořit novou zakázku (saveOrder nevrátilo ID).");
              throw e;
            }

            // nouzové přepárování (aby to hned fungovalo i bez serverového update obřadu)
            try {
              const evId = String(ev?.id || "").trim();
              if (evId) localStorage.setItem(`obrad_link_${evId}`, newId);
            } catch (_err) {}

            await onOpenZakazka(newId);
            return;
          }

          throw e;
        }
      } else {
        // fallback – aspoň si to ulož, kdyby někdo props zapomněl
        try { localStorage.setItem("lastOpenedOrderId", id); } catch (_e) {}
      }

      // 4) přepni do Zakázka → Obřad
      if (typeof onOpenObrad === "function") {
        onOpenObrad();
      }
    } catch (e) {
      console.error("openZakazkaFromEvent error:", e);
      alert("Nepodařilo se otevřít zakázku z kalendáře.");
    }
  }, [onOpenZakazka, onOpenObrad, saveOrder]);

  const [weekBase, setWeekBase] = useState(() => new Date());
  const POSADKY_FIXED = ["Stanko + Novák", "Děda + Burka"];

  const normalizePosadka = (v) => {
    const t = String(v ?? "").trim();
    if (!t) return "";
    if (t === "Šťanko + Novák") return "Stanko + Novák";
    if (t === "A") return "Stanko + Novák";
    if (t === "B") return "Děda + Burka";
    if (t === "C") return "Děda + Burka";
    return t;
  };

  // PER_CLOVEK_HELPERS: kolize per-člověk (posádka + předání rakve)
  const MUZI_FIX = ["Stanko", "Novák", "Děda", "Burka"];

  const normalizeName = (t) => {
    const x = String(t ?? "").trim();
    if (!x) return "";
    // sjednocení starých/špatných zápisů
    return x.replace("Šťanko", "Stanko");
  };

  const posadkaToPeople = (posadkaNazev) => {
    const t = normalizeName(posadkaNazev).trim();

    if (t === "A" || t === "Stanko + Novák") return ["Stanko", "Novák"];
    if (t === "B" || t === "Děda + Burka") return ["Děda", "Burka"];
    if (t === "C") return [];

    const parts = t.split(/\+|,|;|\//).map((x) => normalizeName(x).trim()).filter(Boolean);
    const out = [];
    for (const n of parts) {
      if (MUZI_FIX.includes(n)) out.push(n);
    }
    return out;
  };

  const predaniFrom = (obj) => {
    // podporuje obě varianty názvů z tvého kódu i starší
    const potreba = !!(obj?.prendaniRakev ?? obj?.potrebaPredaniRakve ?? obj?.potrebaPredani ?? false);
    if (!potreba) return "";

    const who = String(obj?.prendaniClovek ?? obj?.predaniClovek ?? "").trim();
    if (!who) return "";

    if (who === "__brig__") {
      const brig = normalizeName(obj?.prendaniBrigadnik ?? obj?.predaniBrigadnik ?? "").trim();
      return brig || "";
    }

    const norm = normalizeName(who).trim();
    return MUZI_FIX.includes(norm) ? norm : norm; // brigádník může být i mimo seznam
  };

const peopleOfForm = (form) => {
    const base = posadkaToPeople(form?.posadkaNazev ?? form?.posadka ?? "");
    const extra = predaniFrom(form);
    return extra ? [...base, extra] : base;
  };

  const overlapPeople = (form, ev) => {
    const A = new Set(peopleOfForm(form));
    const B = peopleOfEvent(ev);
    return B.filter((p) => A.has(p));
  };
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // ✅ seznam posádek – uprav si
  const CREWS = ["Stanko + Novák", "Děda + Burka"];
// ✅ dialog pro kolizi
  const [collision, setCollision] = useState(null); // { blocking, collisions, payload }

  const [form, setForm] = useState({
    id: "",
    datum: "",
    casOd: "10:00",
    casDo: "11:00",
    misto: "",
    typ: "",
    zesnuly: "",
    posadkaNazev: "",
    posadkaLide: [],
    stav: "draft",
    objednavkaId: "",
    poznamka: "",
    predaniClovek: "",
    potrebaPredaniRakve: false,
    autoSpz: "",
    posadkaRucne: "",
    autoRucne: "",
    autoCustomMode: false,
  });
  // AUTO_SET_objednavkaId
  // AUTO_CK_PREDANI: v CK při navazujícím obřadu (časDo == časOd) automaticky vyžádáme muže na předání/výměnu rakve
  useEffect(() => {
    try {
      const misto = String(form.misto ?? "").trim();
      if (misto !== CK_MISTO) return;

      const list = (items ?? []);
      const sameDay = list.filter((e) => String(e.datum) === String(form.datum));

      const navazuje = sameDay.some((e) => {
        const em = String(e.misto ?? "").trim();
        if (em !== misto) return false;
        const aStart = toMinutes(form.casOd);
        const bEnd = toMinutes(e.casDo);
        return !Number.isNaN(aStart) && !Number.isNaN(bEnd) && (bEnd === aStart);
      });

      if (navazuje && !form.potrebaPredaniRakve) {
        setForm((prev) => ({ ...prev, potrebaPredaniRakve: true }));
      }
    } catch (err) {}
  }, [form.misto, form.datum, form.casOd, form.potrebaPredaniRakve]);
  useEffect(() => {
    const id = (String(zakazkaId || "").trim()) || (String(ctxCurrentOrderId || "").trim());
    if (!id) return;
    setForm((f) => (String(f?.objednavkaId || "").trim() ? f : ({ ...f, objednavkaId: id })));
  }, [zakazkaId, ctxCurrentOrderId]);
  const weekStart = useMemo(() => startOfWeek(weekBase), [weekBase]);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const from = fmtDate(weekDays[0]);
  const to = fmtDate(weekDays[6]);

  const itemsByDate = useMemo(() => {
    const map = new Map();
    for (const it of items || []) {
      const d = it?.datum || "";
      if (!map.has(d)) map.set(d, []);
      map.get(d).push(it);
    }
    for (const [k, arr] of map.entries()) {
      arr.sort((a, b) => String(a?.casOd || "").localeCompare(String(b?.casOd || "")));
      map.set(k, arr);
    }
    return map;
  }, [items]);

  const loadWeek = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
            // 🔒 GUARD: backend vyžaduje from/to – pokud jsou prázdné, nespouštěj fetch
      if (!from || !to) {
        try { console.warn("⏭️ OBRADY fetch skipped: missing from/to", { from, to }); } catch {}
        return;
      }

const raw = await apiJson(`/api/obrady?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
      // NORMALIZE_OBRADY_V2 – backend může vracet {ok: !blockingPeople, items:[...]} nebo rovnou [...]
      const itemsRaw =
        Array.isArray(raw) ? raw :
        Array.isArray(raw?.items) ? raw.items :
        Array.isArray(raw?.obrady) ? raw.obrady :
        Array.isArray(raw?.data) ? raw.data :
        [];
      const data = itemsRaw.map((o) => ({
        id: o?.id ?? "",
        datum: o?.datum ?? "",
        casOd: o?.casOd ?? "10:00",
        casDo: o?.casDo ?? "11:00",
        misto: o?.misto ?? "",
        typ: o?.typ ?? "",
        zesnuly: o?.zesnuly ?? "",
        posadkaNazev: o?.posadkaNazev ?? "",
        posadkaLide: Array.isArray(o?.posadkaLide) ? o.posadkaLide : [],
        stav: o?.stav ?? "draft",
        objednavkaId: o?.objednavkaId ?? o?.zakazkaId ?? o?.zakazkaCislo ?? o?.cisloZakazky ?? o?.orderId ?? o?.zakazka?.id ?? "",
        poznamka: o?.poznamka ?? "",
          potrebaPredaniRakve: Boolean(o?.potrebaPredaniRakve ?? o?.prendaniRakev ?? o?.potrebaPredani ?? false),
          predaniClovek: o?.predaniClovek ?? o?.prendaniClovek ?? "",
          autoSpz: o?.autoSpz ?? "",
          posadkaRucne: o?.posadkaRucne ?? "",
          autoRucne: o?.autoRucne ?? "",
          autoCustomMode: Boolean(o?.autoCustomMode ?? false),
}));

      setItems(data);
    } catch (e) {
      setErr(e?.message || "Chyba načtení obřadů");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    loadWeek();
  }, [loadWeek]);

  // ✅ AUTO předvyplnění z Obřadu / kontextu
  useEffect(() => {
    if (form.id) return; // při editaci nepřepisuj

    const o = formData?.obrad || {};
    const kp = formData?.kremacePohreb || {};

    const autoDatum = pick(o?.datum, kp?.obradDatum, kp?.datumObradu, "");
    const autoCas = pick(o?.cas, kp?.obradCas, kp?.casObradu, "");
    const autoMisto = pick(o?.vystav, o?.misto, kp?.mistoObradu, "");
    const autoTyp = pick(o?.typ, kp?.typObradu, kp?.obradTyp, kp?.typ, "");
    const autoZesnuly = buildZesnulyLabel(formData);

    setForm((prev) => {
      const next = { ...prev };
      if (!pick(prev.datum) && autoDatum) next.datum = autoDatum;
      if (!pick(prev.misto) && autoMisto) next.misto = autoMisto;
      if (!pick(prev.typ) && autoTyp) next.typ = autoTyp;
      if (!pick(prev.zesnuly) && autoZesnuly) next.zesnuly = autoZesnuly;

      if (!pick(prev.casOd) && autoCas) next.casOd = autoCas;
      if (!pick(prev.casDo) && autoCas) {
        const [hh, mm] = autoCas.split(":").map((x) => Number(x));
        if (Number.isFinite(hh) && Number.isFinite(mm)) {
          const dt = new Date(2000, 0, 1, hh, mm);
          dt.setMinutes(dt.getMinutes() + 60);
          next.casDo = `${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
        }
      }

      // ✅ doplň objednavkaId z ctxCurrentOrderId (ale jen když je prázdné)
      // ✅ AUTO_SET posádka + SPZ z formData (jen když je prázdné / ručně nepřepsané)
      const fdPosadka =
        formData?.prevoz?.posadkaNazev ||
        formData?.prevoz?.posadka ||
        formData?.vyjezdy?.posadkaNazev ||
        formData?.vyjezdy?.posadka ||
        formData?.obrad?.posadkaNazev ||
        formData?.obrad?.posadka ||
        "";

      const fdSpz =
        formData?.prevoz?.autoSpz ||
        formData?.prevoz?.spz ||
        formData?.prevoz?.spzVozidla ||
        formData?.vyjezdy?.autoSpz ||
        formData?.vyjezdy?.spz ||
        formData?.obrad?.autoSpz ||
        formData?.obrad?.spz ||
        "";

      // posádka: nelez do toho, když už to je vyplněné nebo má ruční override
      if (!pick(prev.posadkaNazev) && !pick(prev.posadkaRucne) && pick(fdPosadka)) {
        next.posadkaNazev = normalizePosadka(String(fdPosadka));
      }
      // lidi dopočítáme jen když nejsou a máme název posádky
      if ((!Array.isArray(prev.posadkaLide) || prev.posadkaLide.length == 0) && pick(next.posadkaNazev)) {
        next.posadkaLide = posadkaToPeople(next.posadkaNazev);
      }

      // SPZ: nelez do toho, když už to je vyplněné nebo má ruční override
      if (!pick(prev.autoSpz) && !pick(prev.autoRucne) && pick(fdSpz)) {
        next.autoSpz = String(fdSpz).trim();
      }

      if (!pick(prev.objednavkaId) && pick(ctxCurrentOrderId)) next.objednavkaId = String(ctxCurrentOrderId);

      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, ctxCurrentOrderId]);

  function resetForm() {
    setCollision(null);
    setForm({
      id: "",
      datum: "",
      casOd: "10:00",
      casDo: "11:00",
      misto: "",
      typ: "",
      zesnuly: "",
      posadkaNazev: "",
      posadkaLide: [],
      stav: "draft",
      objednavkaId: pick(ctxCurrentOrderId, ""),
      poznamka: "",
        potrebaPredaniRakve: false,
        predaniClovek: "",
        autoSpz: "",
        posadkaRucne: "",
        autoRucne: ""});
  }

  async function validateCollision(payload) {
    try {
    payload.objednavkaId = String(payload.objednavkaId || zakazkaId || form.objednavkaId || ctxCurrentOrderId || "");
      return await apiJson("/api/obrady/validate", {
        method: "POST",
        body: JSON.stringify(payload)});
    } catch {
      return null;
    }
  }

  // === UX: Conflict modal (validateCollision) ===
  const [conflictModalOpen, setConflictModalOpen] = useState(false);
  const [conflictModalData, setConflictModalData] = useState(null); // { payload, blocking, warning, conflicts, raw }
  const [skipValidateOnce, setSkipValidateOnce] = useState(false);

  const openConflictModal = (payload, check) => {
    setConflictModalData({
      payload: payload || null,
      blocking: check?.blocking ?? null,
      warning: check?.warning ?? null,
      conflicts: Array.isArray(check?.conflicts) ? check.conflicts : (Array.isArray(check?.collisions) ? check.collisions : []),
      raw: check || null,
    });
    setConflictModalOpen(true);
  };

  const closeConflictModal = () => {
    setConflictModalOpen(false);
  };

  const patchConflictPayload = (patch) => {
    setConflictModalData((x) => {
      const p = { ...(x?.payload || {}) };
      for (const k in patch) p[k] = patch[k];
      return { ...(x || {}), payload: p };
    });
  };

  const retryValidateFromModal = async () => {
    try {
      const payload = conflictModalData?.payload || {};
      const check = await validateCollision(payload);

      if (check && check.ok === false) {
        openConflictModal(payload, check);
        return;
      }

      setConflictModalData({
        payload,
        blocking: check?.blocking ?? null,
        warning: check?.warning ?? null,
        conflicts: Array.isArray(check?.conflicts) ? check.conflicts : (Array.isArray(check?.collisions) ? check.collisions : []),
        raw: check || null,
      });

      const count = (Array.isArray(check?.conflicts) ? check.conflicts.length : (Array.isArray(check?.collisions) ? check.collisions.length : 0));
      if (!check?.blocking && !check?.warning && count === 0) {
        closeConflictModal();
      }
    } catch (e) {
      console.error(e);
      alert("Validace selhala (viz konzole).");
    }
  };

  const continueAnyway = async () => {
    setSkipValidateOnce(true);
    closeConflictModal();
    try {
      await saveObrad();
    } catch (e) {}
  };



  async function doSave(payload) {
    payload.objednavkaId = String(payload.objednavkaId || zakazkaId || form.objednavkaId || ctxCurrentOrderId || "");
    const oid = await ensureObjednavkaId({ form, zakazkaId, ctxCurrentOrderId, formData, saveOrder });
    if (!oid) throw new Error("CHYBÍ objednavkaId");

    const payloadWithObjId = { ...payload, objednavkaId: oid };

    // STOP: kolize posádky (stejný člověk na 2 obřadech ve stejný čas)
    try {
      const msg = validatePosadkaCollision({ edited: payloadWithObjId, allEvents: items });
      if (msg) {
        alert(msg);
        return;
      }
    } catch (e) {
      console.warn("validatePosadkaCollision failed", e);
    }

    await apiJson("/api/obrady", { method: "POST", body: JSON.stringify(payloadWithObjId) });
    await loadWeek();
    resetForm();
  }

  // ✅ KLÍČ: zajistí existenci ID zakázky (objednavkaId)
  const ensureOrderIdForCalendar = useCallback(async () => {
    // 1) už vyplněné v poli
    const existing = pick(form.objednavkaId);
    if (existing) return existing;

    // 2) máme ctxCurrentOrderId v kontextu
    const ctxId = pick(ctxCurrentOrderId);
    if (ctxId) {
      setForm((p) => ({ ...p, objednavkaId: ctxId }));
      return ctxId;
    }

    // 3) nemáme nic -> založ zakázku v knize (server vytvoří ID)
    if (typeof saveOrder !== "function") {
      throw new Error("Chybí saveOrder() – nelze založit zakázku pro kalendář.");
    }

    const prijmeni = formData?.vstupniInformace?.prijmeniZesnuleho || "";
    const jmeno = formData?.vstupniInformace?.jmenoZesnuleho || "";
    const datumUmrti =
      formData?.vstupniInformace?.datumUmrti || formData?.vyjezdy?.datumUmrti || "";

    const meta = await saveOrder({
      id: null,
      prijmeni,
      jmeno,
      datumUmrti,
      slozkaSablon: formData?.slozkaSablon || "kremace",
      stavZakazky: formData?.stavZakazky || "rozpracovana",
      formData});

    const newId = pick(meta?.id, meta?.meta?.id);
    if (!newId) throw new Error("Nepodařilo se založit zakázku (server nevrátil id).");

    setForm((p) => ({ ...p, objednavkaId: newId }));
    return newId;
  }, [form.objednavkaId, ctxCurrentOrderId, saveOrder, formData]);

  async function saveObrad() {

    // === VALIDACE kolizí (před uložením) ===
    if (!skipValidateOnce) {
      const payload = {
        id: form.id || null,
        datum: form.datum || "",
        casOd: form.casOd || "",
        casDo: form.casDo || "",
        misto: form.misto || "",
        posadkaNazev: form.posadkaNazev || "",
        autoSpz: form.autoSpz || "",
        typ: form.typ || "",
        objednavkaId: form.objednavkaId || "",
      };

      const check = await validateCollision(payload);

      if (check && check.ok === false) {
        openConflictModal(payload, check);
        return;
      }

      if (check && check.ok === true && check.warning) {
        openConflictModal(payload, check);
        return;
      }
    }

    if (skipValidateOnce) setSkipValidateOnce(false);


    setErr("");

    if (!form.datum || !form.casOd || !form.casDo) {
      setErr("Vyplň datum + čas od/do.");
      return;
    }

    let orderId = "";
    try {
      orderId = await ensureOrderIdForCalendar();
    } catch (e) {
      setErr(e?.message || "Nepodařilo se založit/načíst zakázku pro kalendář.");
      return;
    }

    const payload = {
      ...form,
      posadkaNazev: normalizePosadka(form.posadkaNazev),
      typ: pick(form.typ, buildTypLabel(formData)),
      zesnuly: pick(form.zesnuly, buildZesnulyLabel(formData)),
      objednavkaId: orderId};

try {
      await doSave(payload);
    } catch (e) {
      setErr(e?.message || "Chyba uložení obřadu");
    }
  }

  function editItem(it) {
    setCollision(null);
    setForm({
      id: it.id || "",
      datum: it.datum || "",
      casOd: it.casOd || "10:00",
      casDo: it.casDo || "11:00",
      misto: it.misto || "",
      typ: it.typ || "",
      zesnuly: it.zesnuly || "",
      posadkaNazev: it.posadkaNazev || "",
      posadkaLide: Array.isArray(it.posadkaLide) ? it.posadkaLide : [],
      stav: it.stav || "draft",
      objednavkaId: it.objednavkaId || "",
      poznamka: it.poznamka || ""});
  }

  // ✅ fallback – když není App.jsx napojený, zkusíme načíst zakázku přímo
  
    // ✅ ensure (backend): když zakázka v knize neexistuje, automaticky ji založ
    async function ensureZakazka(zid, ctx = {}) {
      const id = pick(zid).trim();
      if (!id) throw new Error("Chybí objednavkaId.");
      const payload = { id, zesnuly: ctx.zesnuly || "", typ: ctx.typ || "", datum: ctx.datum || "" };
      return await apiJson(`/api/kniha/ensure`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    }

async function tryLoadZakazkaDirect(id) {
    const zid = pick(id).trim();
    if (!zid) throw new Error("Chybí objednavkaId.");

    const tries = [
      `/api/kniha/nacist/${encodeURIComponent(zid)}`, // ✅ jediný správný endpoint
    ];

    let lastErr = null;
    for (const url of tries) {
      try {
        const data = await apiJson(url, { method: "GET" });
        if (data) return { url, data };
      } catch (e) {
        lastErr = e;
      }
    }
    throw new Error(lastErr?.message || "Zakázku se nepodařilo načíst (nenašel jsem endpoint).");
  }

  async function openZakazkaFromCalendar(id) {
    const zid = pick(id).trim();
    if (!zid) {
      setErr("U obřadu chybí objednavkaId.");
      return;
    }

    try {
      setErr("");

      // 1) standard – přepne tab a načte zakázku v App.jsx
      if (typeof onOpenZakazka === "function") {
        await onOpenZakazka(zid);
        return;
      }

      // 2) fallback – načti aspoň data ze serveru a vypiš do konzole
      const out = await tryLoadZakazkaDirect(zid);
      console.log("DIRECT LOAD OK:", out.url, out.data);
      setErr(`⛔ FALLBACK MODE: onOpenZakazka NENÍ funkce. (Direct load: ${out.url})`);
    } catch (e) {
      console.error("openZakazkaFromCalendar error:", e);
      setErr(e?.message || "Otevření zakázky selhalo.");
    }
  }

  return (
    <>
      <div style={{ display: "grid", gap: 12 }}>
      {/* NAV */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <button type="button" style={btn} onClick={() => setWeekBase(addDays(weekBase, -7))}>
          ← Minulý týden
        </button>
        <button type="button" style={btn} onClick={() => setWeekBase(new Date())}>
          Dnes
        </button>
        <button type="button" style={btn} onClick={() => setWeekBase(addDays(weekBase, 7))}>
          Další týden →
        </button>
        <div style={{ fontWeight: 900 }}>
          Týden: {from} → {to}
        </div>
        <button type="button" style={{ ...btn, borderColor: "#999" }} onClick={loadWeek}>
          {loading ? "Načítám..." : "Obnovit"}
        </button>
      </div>

      {err ? <div style={{ ...card, borderColor: "#b00", color: "#b00", fontWeight: 900 }}>{err}</div> : null}

      {/* ✅ DIALOG kolize */}
      {collision ? (
        <div style={{ ...card, borderColor: "#b00" }}>
          <div style={{ fontWeight: 1000, color: "#b00", marginBottom: 6 }}>⛔ Kolize posádky</div>
          <div style={{ fontWeight: 900, marginBottom: 8 }}>{collision.blocking}</div>

          {collision?.collisions?.length ? (
            <div style={{ fontSize: 13, marginBottom: 10 }}>
              <div style={{ fontWeight: 900, marginBottom: 4 }}>Koliduje s:</div>
              <div style={{ display: "grid", gap: 6 }}>
                {collision.collisions.map((c) => (
                  <div key={c.id} style={{ padding: 8, borderRadius: 10, border: "1px solid #eee" }}>
                    <b>
                      {c.datum} {c.casOd}–{c.casDo}
                    </b>{" "}
                    • {c.zesnuly || "-"} • {c.misto || ""} • Posádka: <b>{c.posadkaNazev || "-"}</b>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div style={{ display: "grid", gap: 8, maxWidth: 420 }}>
            <span style={label}>Vyber jinou posádku</span>
            <select
              style={input}
              value={collision.payload.posadkaNazev || ""}
              onChange={(e) => setCollision((x) => ({ ...x, payload: { ...x.payload, posadkaNazev: normalizePosadka(e.target.value) } }))}
            >
              <option value="">— vyber —</option>
              {CREWS.map((c) => (
                <option key={c} value={c === "Jiná..." ? "" : c}>
                  {c}
                </option>
              ))}
            </select>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                type="button"
                style={btn}
                onClick={async () => {
                  try {
                    const p = collision.payload;
                    const check = await validateCollision({
                      id: p.id || undefined,
                      datum: p.datum,
                      casOd: p.casOd,
                      casDo: p.casDo,
                      misto: p.misto,
                      autoSpz: p.autoSpz,
                      posadkaRucne: p.posadkaRucne,
                      autoRucne: p.autoRucne,
                      posadkaNazev: p.posadkaNazev});
                    if (check?.blocking) {
                      setCollision({
                        blocking: check.blocking,
                        collisions: Array.isArray(check.collisions) ? check.collisions : [],
                        payload: p});
                      return;
                    }
                    if (check?.warning) {
                      const ok = window.confirm(`${check.warning}\nChceš uložit i tak?`);
                      if (!ok) return;
                    }
                    await doSave(p);
                    setCollision(null);
                  } catch (e) {
                    setErr(e?.message || "Chyba uložení");
                  }
                }}
              >
                Uložit s jinou posádkou
              </button>

              <button type="button" style={{ ...btn, borderColor: "#999" }} onClick={() => setCollision(null)}>
                Zavřít
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div style={{ display: "grid", gridTemplateColumns: mode === "minimal" ? "1fr" : "1fr 1fr", gap: 12 }}>
        {/* LEVÁ: formulář */}
{mode !== "minimal" ? (
<div style={card}>
          <div style={{ fontWeight: 1000, fontSize: 16, marginBottom: 10 }}>Přidat / upravit obřad</div>

          <div style={{ display: "grid", gap: 10 }}>
            <div>
              <span style={label}>Datum</span>
              <input style={input} type="date" value={form.datum} onChange={(e) => setForm({ ...form, datum: e.target.value })} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div>
                <span style={label}>Čas od</span>
                <input style={input} type="time" value={form.casOd} onChange={(e) => setForm({ ...form, casOd: e.target.value })} />
              </div>
              <div>
                <span style={label}>Čas do</span>
                <input style={input} type="time" value={form.casDo} onChange={(e) => setForm({ ...form, casDo: e.target.value })} />
              </div>
            </div>

            <div>
              <span style={label}>Zesnulý</span>
              <input style={input} value={form.zesnuly} onChange={(e) => setForm({ ...form, zesnuly: e.target.value })} placeholder="Příjmení Jméno" />
              <div style={hint}>Tip: automaticky se doplní ze záložek (Zesnulá osoba / Vstupní informace).</div>
            </div>

            <div>
              <span style={label}>Místo</span>
              <input style={input} value={form.misto} onChange={(e) => setForm({ ...form, misto: e.target.value })} placeholder="Kaple / Kostel / Krematorium..." />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div>
                <span style={label}>Typ</span>
                <input style={input} value={form.typ} onChange={(e) => setForm({ ...form, typ: e.target.value })} placeholder="Kremace / Pohřeb / Rozloučení..." />
              </div>
              <div>
                <span style={label}>Posádka</span>
                <input style={input} value={form.posadkaNazev} onChange={(e) => setForm({ ...form, posadkaNazev: normalizePosadka(e.target.value) })} placeholder="A / B / Roman+Maxa..." />
              </div>
            </div>

            <div>
              <span style={label}>ID zakázky (objednavkaId)</span>
              <input style={input} value={form.objednavkaId} onChange={(e) => setForm({ ...form, objednavkaId: e.target.value })} placeholder="automaticky se doplní" />
              <div style={hint}>Když je prázdné, při uložení obřadu se zakázka sama založí.</div>
            </div>

            <div>
              <span style={label}>Poznámka</span>
              <input style={input} value={form.poznamka} onChange={(e) => setForm({ ...form, poznamka: e.target.value })} placeholder="speciální info..." />
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button type="button" style={btn} onClick={saveObrad}>
                {form.id ? "Uložit změny" : "Uložit obřad"}
              </button>

              <button type="button" style={{ ...btn, borderColor: "#999" }} onClick={resetForm}>
                Nový
              </button>

              <button
                type="button"
                style={{ ...btn, borderColor: "#666" }}
                onClick={async (e) => {
                  e.preventDefault();
                  await openZakazkaFromEvent(form);
                }}
                disabled={!pick(form.objednavkaId)}
                title={!pick(form.objednavkaId) ? "Vyplň objednavkaId" : "Otevřít zakázku"}
              >
                Otevřít zakázku
              </button>
            </div>

            {form.id ? (
              <div style={{ fontSize: 12, opacity: 0.8 }}>
                Edituješ: <b>{form.id}</b>
              </div>
            ) : null}
          </div>
        </div>
) : null}

{/* PRAVÁ: týdenní přehled */}
        <div style={card}>          <div style={{ fontWeight: 1000, fontSize: 16, marginBottom: 10 }}>Týdenní přehled</div>

          <div style={{ display: "grid", gap: 10 }}>
            {weekDays.map((d) => {
              const ds = fmtDate(d);
              const list = itemsByDate.get(ds) || [];
              return (
                <div key={ds} style={{ border: "1px solid #eee", borderRadius: 12, padding: 10 }}>
                  <div style={{ fontWeight: 1000, marginBottom: 6 }}>
                    {ds} ({["Po", "Út", "St", "Čt", "Pá", "So", "Ne"][(d.getDay() + 6) % 7]})
                  </div>

                  {list.length === 0 ? (
                    <div style={{ opacity: 0.6 }}>— bez obřadů —</div>
                  ) : (
                    <div style={{ display: "grid", gap: 6 }}>
                      {list.map((it) => (
                        <div
                          key={it.id}
                          style={{
                            padding: 10,
                            borderRadius: 12,
                            border: "1px solid #ddd",
                            display: "grid",
                            gap: 2}}
                        >
                          <div style={{ fontWeight: 1000 }}>
                            {it.casOd}–{it.casDo} • {it.zesnuly || "(bez jména)"}
                          </div>
                          <div style={{ fontSize: 13, opacity: 0.9 }}>
                            {it.misto || ""}
                            {it.typ ? ` • ${it.typ}` : ""}
                            {it.posadkaNazev ? ` • Posádka: ${it.posadkaNazev}` : ""}
                          </div>

                          <div style={{ fontSize: 12, opacity: 0.7 }}>
                            stav: <b>{it.stav || "draft"}</b> • id: {it.id}
                            {it.objednavkaId ? ` • zakázka: ${it.objednavkaId}` : " • zakázka: (CHYBÍ objednavkaId)"}
                          </div>

                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
                            <button
                              type="button"
                              style={{ ...btn, padding: "6px 10px", borderColor: "#999" }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                editItem(it);
                              }}
                            >
                              Upravit
                            </button>

                            <button
                              type="button"
                              style={{ ...btn, padding: "6px 10px", borderColor: "#666" }}
                              disabled={!pick(it.objednavkaId)}
                              onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                await openZakazkaFromEvent(it);
                              }}
                              title={!pick(it.objednavkaId) ? "Chybí objednavkaId" : "Otevřít zakázku"}
                            >
                              Otevřít zakázku
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>

      
      
      {conflictModalOpen && (
        <ConflictModal
          open={!!conflictModalOpen}
          data={conflictModalData}
          onClose={closeConflictModal}
          onRetry={retryValidateFromModal}
          onPatchPayload={patchConflictPayload}
          onContinueAnyway={typeof continueAnyway === "function" ? continueAnyway : undefined}
        />
      )}
    </>
  );
}
